import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'
import PageTitle from '../../_Components/Common/PageTitle/PageTitle'
import { DMM_API_HOST, DMM_API_KEY } from '../../_Components/Common/common'

/*
 * FANZA 이미지·영상 기능의 기간별 지표.
 * 집계는 fanza-api 의 /v4.0.1/admin-data/image-video 가 전부 담당하고 여기서는 표시만 한다.
 */
// 오늘로부터 n일 전 날짜를 YYYY-MM-DD 로. 프리셋 버튼과 기본값이 함께 쓴다.
// toISOString 은 UTC 라 KST 오전 9시 이전에 하루 밀린다 — 로컬 날짜로 직접 조립한다.
const dayAgo = n => {
  const date = new Date()
  date.setDate(date.getDate() - n)
  const pad = v => String(v).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const PRESETS = [
  { id: 'today', label: '오늘', days: 1 },
  { id: 'week', label: '1주일', days: 7 },
  { id: 'month', label: '1달', days: 30 },
]

const ImageVideoData = () => {
  // 입력칸은 초안이고, 실제 조회는 applied 로만 돈다. 조회 1회가 큰 테이블을 통째로 훑기 때문에
  // 날짜를 만지는 도중의 어중간한 구간(시작일만 옮긴 상태 등)으로는 요청을 보내지 않는다.
  const [startDate, setStartDate] = useState(dayAgo(6))
  const [endDate, setEndDate] = useState(dayAgo(0))
  const [applied, setApplied] = useState({ startDate: dayAgo(6), endDate: dayAgo(0) })
  const [rangeLabel, setRangeLabel] = useState('')
  const [prevLabel, setPrevLabel] = useState('')
  const [list, setList] = useState([])
  const [media, setMedia] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const requestSeq = useRef(0)

  useEffect(() => {
    getData(applied)
  }, [applied])

  const invalidRange = !startDate || !endDate || startDate > endDate

  const isDirty =
    startDate !== applied.startDate || endDate !== applied.endDate

  const search = () => {
    if (invalidRange || !isDirty) return
    setApplied({ startDate, endDate })
  }

  // 프리셋은 한 번 누르는 것이 곧 의도라 초안과 조회를 함께 갱신한다.
  const applyPreset = days => {
    const next = { startDate: dayAgo(days - 1), endDate: dayAgo(0) }
    setStartDate(next.startDate)
    setEndDate(next.endDate)
    setApplied(next)
  }

  const activePreset = PRESETS.find(
    p =>
      applied.startDate === dayAgo(p.days - 1) &&
      applied.endDate === dayAgo(0) &&
      !isDirty
  )

  const getData = async range => {
    setLoading(true)
    setError('')

    // 넓은 구간은 20초까지 걸린다. 늦게 온 이전 요청이 최신 결과를 덮어쓰지 않게 순번을 확인한다.
    const seq = ++requestSeq.current
    const isStale = () => seq !== requestSeq.current

    await axios
      .get(
        `${DMM_API_HOST}/v4.0.1/admin-data/image-video?startDate=${range.startDate}&endDate=${range.endDate}`,
        { headers: { apikey: DMM_API_KEY } }
      )
      .then(({ data }) => {
        if (isStale()) return
        const range = (data.body && data.body.range) || {}
        setList((data.body && data.body.list) || [])
        setRangeLabel(range.label || '')
        setPrevLabel(
          range.prevStartDate ? `${range.prevStartDate} ~ ${range.prevEndDate}` : ''
        )
      })
      .catch(err => {
        if (isStale()) return
        console.error('[admin-data/image-video] error:', err)
        const msg = err.response && err.response.data && err.response.data.message
        setError(msg ? `데이터를 불러오지 못했습니다: ${msg}` : '데이터를 불러오지 못했습니다.')
      })
      .finally(() => {
        if (!isStale()) setLoading(false)
      })
  }

  const rows = list.filter(row => media === 'all' || row.media === media)

  const totals = rows.reduce(
    (acc, row) => ({
      spend: acc.spend + (row.spend || 0),
      count: acc.count + (row.count || 0),
      errors: acc.errors + (row.errors || 0),
    }),
    { spend: 0, count: 0, errors: 0 }
  )

  const num = value =>
    value === null || value === undefined ? '-' : value.toLocaleString()

  const renderGrowth = value => {
    if (value === null || value === undefined) return <Muted>신규</Muted>
    if (value === 0) return <Muted>0%</Muted>
    return <Growth up={value > 0}>{value > 0 ? `+${value}%` : `${value}%`}</Growth>
  }

  return (
    <Wrapper>
      <PageTitle
        mainTitle={'Image/Video Data'}
        v1Link={''}
        navNames={['Data Analytics', 'Image/Video Data']}
      />

      <Controls>
        <Presets>
          {PRESETS.map(preset => (
            <PresetButton
              key={preset.id}
              active={activePreset && activePreset.id === preset.id}
              onClick={() => applyPreset(preset.days)}
            >
              {preset.label}
            </PresetButton>
          ))}
        </Presets>

        <DateRange onKeyDown={e => e.key === 'Enter' && search()}>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <span>~</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={dayAgo(0)}
            onChange={e => setEndDate(e.target.value)}
          />
          <SearchButton
            ready={isDirty && !invalidRange}
            disabled={!isDirty || invalidRange || loading}
            onClick={search}
          >
            조회
          </SearchButton>
        </DateRange>

        <select value={media} onChange={e => setMedia(e.target.value)}>
          <option value="all">전체</option>
          <option value="image">이미지</option>
          <option value="video">영상</option>
        </select>

        <Summary>
          {rangeLabel && <strong>{rangeLabel}</strong>}
          {invalidRange ? (
            <Pending>시작일이 종료일보다 늦습니다.</Pending>
          ) : (
            isDirty && <Pending>조회를 눌러야 반영됩니다.</Pending>
          )}
          {!loading && !isDirty && (
            <span>
              생성/판매 {num(totals.count)}건 · 오류 {num(totals.errors)}건 · 소비{' '}
              {num(totals.spend)}pt
            </span>
          )}
        </Summary>
      </Controls>

      {error && <Message>{error}</Message>}
      {loading && !error && <Message>불러오는 중…</Message>}

      {!loading && !error && (
        <TableBox>
          <Table>
            <thead>
              <tr>
                <Th align="left">기능</Th>
                <Th align="left">모델</Th>
                <Th align="left">플랫폼</Th>
                <Th>소비 포인트</Th>
                <Th>유저 수</Th>
                <Th>건수</Th>
                <Th>인당 평균</Th>
                <Th>오류</Th>
                <Th title="성공도 실패도 아닌 채로 멈춘 건">미완료</Th>
                <Th>성공률</Th>
                <Th>재구매 유저</Th>
                <Th>1회 유저</Th>
                <Th>최다 구매</Th>
                {/* 비교 대상은 바로 앞의 같은 길이 기간 */}
                <Th title={prevLabel && `직전 기간 ${prevLabel}`}>직전 기간 대비</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <Td align="left">
                    <Name>
                      <MediaTag video={row.media === 'video'}>
                        {row.media === 'video' ? '영상' : '이미지'}
                      </MediaTag>
                      {row.name}
                    </Name>
                    {row.note && <Note>{row.note}</Note>}
                  </Td>

                  {row.available === false ? (
                    <Td align="left" colSpan={13}>
                      <Muted>{row.note}</Muted>
                    </Td>
                  ) : (
                    <>
                      <Td align="left">
                        {(row.models || []).map(model => (
                          <div key={model}>{model}</div>
                        ))}
                        {row.modelMix && (
                          <Note>
                            {row.modelMix
                              .map(mix => `${mix.platform} ${mix.count}건`)
                              .join(' · ')}
                          </Note>
                        )}
                      </Td>
                      <Td align="left">{row.platform}</Td>
                      <Td>
                        {row.spend ? `${num(row.spend)}${row.spendUnit || ''}` : '-'}
                      </Td>
                      <Td>{num(row.users)}</Td>
                      <Td>
                        <strong>{num(row.count)}</strong>
                      </Td>
                      <Td>{num(row.avgPerUser)}</Td>
                      <Td>
                        {row.errors ? <Bad>{num(row.errors)}</Bad> : '-'}
                        {/* 다른 모델로 다시 시도해 결국 성공한 건 — 오류와 구분해서 보여준다 */}
                        {!!row.retries && <Note>재시도 {num(row.retries)}</Note>}
                      </Td>
                      <Td>{row.pending ? <Warn>{num(row.pending)}</Warn> : '-'}</Td>
                      <Td>
                        {row.successRate === null ? '-' : `${row.successRate}%`}
                      </Td>
                      <Td>{num(row.repeatUsers)}</Td>
                      <Td>{num(row.singleUsers)}</Td>
                      <Td>{num(row.maxPerUser)}</Td>
                      <Td>{renderGrowth(row.growth && row.growth.count)}</Td>
                    </>
                  )}
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <Td colSpan={14}>
                    <Muted>표시할 기능이 없습니다.</Muted>
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableBox>
      )}

      <Footnote>
        「소비 포인트」는 선택한 기간에 실제로 차감된 DMM 포인트입니다. 포인트
        코드 하나를 두 기능이 함께 쓰는 경우(캐릭터 생성 · 스타일 라이브러리,
        무드샷 신규 · 이어그리기)는 단가와 건수로 나눠 배분합니다. 「재구매
        유저」는 그 기간에 2회 이상 사용한 유저 수입니다. 「직전 기간 대비」는
        바로 앞의 같은 길이 기간
        {prevLabel ? `(${prevLabel})` : ''}과 비교한 건수 증감입니다. 무드샷은 한
        모델이 실패하면 다른 모델로 다시 생성하므로, 결국 이미지가 나온 건은
        「오류」가 아니라 「재시도」로 셉니다 — 성공률도 최종 실패만 반영합니다.
        「미완료」는 성공도 실패도 아닌 채로 남은 건으로, 완료 콜백을 받지 못해
        멈춘 것입니다. 건수·오류·성공률 어디에도 들어가지 않습니다.
      </Footnote>
    </Wrapper>
  )
}

export default ImageVideoData

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 15px 30px;
  box-sizing: border-box;
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 10px;
  row-gap: 8px;
  margin: 15px 0;

  select {
    height: 30px;
  }
`

const Presets = styled.div`
  display: flex;
`

const PresetButton = styled.button`
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${props => (props.active ? '#3c8dbc' : '#d2d6de')};
  background-color: ${props => (props.active ? '#3c8dbc' : '#fff')};
  color: ${props => (props.active ? '#fff' : '#555')};
  font-size: 12px;
  cursor: pointer;

  & + & {
    margin-left: -1px;
  }

  &:first-child {
    border-radius: 3px 0 0 3px;
  }

  &:last-child {
    border-radius: 0 3px 3px 0;
  }
`

const DateRange = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-size: 12px;
  color: #888;

  input {
    height: 30px;
    padding: 0 8px;
    border: 1px solid #d2d6de;
    border-radius: 3px;
    font-size: 12px;
    color: #555;
  }
`

const SearchButton = styled.button`
  height: 30px;
  margin-left: 4px;
  padding: 0 14px;
  border: 1px solid ${props => (props.ready ? '#00a65a' : '#d2d6de')};
  border-radius: 3px;
  background-color: ${props => (props.ready ? '#00a65a' : '#f4f4f4')};
  color: ${props => (props.ready ? '#fff' : '#aaa')};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => (props.ready ? 'pointer' : 'default')};
`

const Pending = styled.span`
  color: #f39c12;
`

const Summary = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  margin-left: 10px;
  font-size: 13px;
  color: #444;
`

const Message = styled.div`
  padding: 30px 0;
  color: #888;
  font-size: 13px;
`

const TableBox = styled.div`
  width: 100%;
  overflow-x: auto;
  background-color: #fff;
  border: 1px solid #d2d6de;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  tbody tr:nth-child(even) {
    background-color: #fafafa;
  }
`

const Th = styled.th`
  padding: 8px 10px;
  border-bottom: 2px solid #d2d6de;
  text-align: ${props => props.align || 'right'};
  white-space: nowrap;
  color: #555;
  font-weight: 600;
`

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  text-align: ${props => props.align || 'right'};
  white-space: ${props => (props.align === 'left' ? 'normal' : 'nowrap')};
  vertical-align: top;
`

const Name = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-weight: 600;
`

const MediaTag = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  background-color: ${props => (props.video ? '#8e44ad' : '#3c8dbc')};
`

const Note = styled.div`
  margin-top: 3px;
  color: #999;
  font-size: 11px;
  font-weight: 400;
  max-width: 380px;
`

const Muted = styled.span`
  color: #aaa;
`

const Bad = styled.span`
  color: #dd4b39;
  font-weight: 600;
`

const Warn = styled.span`
  color: #f39c12;
  font-weight: 600;
`

const Growth = styled.span`
  color: ${props => (props.up ? '#00a65a' : '#dd4b39')};
  font-weight: 600;
`

const Footnote = styled.p`
  margin-top: 12px;
  color: #888;
  font-size: 11px;
  line-height: 1.6;
`
